/**
 * JLR SharePoint 交互认证弹窗的第一步（Pike 项目）。
 *
 * 该站点强制 MFA：密码直连会被 AADSTS50076 拒绝，用户必须在 Microsoft
 * Authenticator 里输入登录页上显示的数字并批准。所以「上传第三方平台」拆成两步：
 * 这里只填账号密码、拿号码、等批准；登录成功后关掉本弹窗，进入第二步选路径。
 *
 * 密码只进这一次请求，不回显、不落任何本地存储。
 */

import { useEffect, useRef, useState } from 'react';
import { Alert, Button, Form, Input, Modal, Space, Typography } from 'antd';
import { KeyOutlined, SafetyCertificateOutlined } from '@ant-design/icons';
import type { JlrAuthStartResult } from '@/types';
import { confirmJlrAuth, startJlrAuth } from '@/api/thirdPartyUpload';
import { useT } from '@/i18n';

const { Text } = Typography;

/** 后端 `config.py` 里的默认父目录；第二步弹窗直接用它做默认值 */
export const DEFAULT_JLR_PARENT_URL =
  'https://jlrglobal.sharepoint.com/sites/jlr-momenta-aad/Shared%20Documents/Forms/AllItems.aspx' +
  '?id=%2Fsites%2Fjlr%2Dmomenta%2Daad%2FShared%20Documents%2F01%5FSupplier%20Exchange' +
  '%2F02%5FBack%20JLR%20From%20Momenta%2F05%20Integration%2Fpackage%5Frelease' +
  '&viewid=3572b730%2D9b6c%2D4e30%2D91f8%2D1a2812c4f9a6';

interface JlrAuthFormValues {
  username: string;
  password: string;
}

interface JlrAuthModalProps {
  open: boolean;
  releaseId: number;
  /** 登录已确认：把会话句柄交给第二步弹窗 */
  onAuthenticated: (sessionId: string) => void;
  onCancel: () => void;
}

interface ApiErrorBody {
  error?: string;
  message?: string;
}

const errorBody = (e: unknown): ApiErrorBody =>
  (e as { response?: { data?: ApiErrorBody } })?.response?.data ?? {};

export const JlrAuthModal: React.FC<JlrAuthModalProps> = ({
  open,
  releaseId,
  onAuthenticated,
  onCancel,
}) => {
  const { t } = useT();
  const [form] = Form.useForm<JlrAuthFormValues>();
  // 'credentials' = 填账号密码；'approve' = 等用户在手机上输数字并批准
  const [step, setStep] = useState<'credentials' | 'approve'>('credentials');
  const [submitting, setSubmitting] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [error, setError] = useState('');
  const [pending, setPending] = useState(false);
  const [auth, setAuth] = useState<JlrAuthStartResult | null>(null);
  // 只留回填需要的用户名：密码不留在 React state 里，返回修改时要重新输入
  const [username, setUsername] = useState('');
  // 每次重新打开都是新的会话；旧的会话 ID 已经过期
  const sessionRef = useRef('');
  // 自动确认只跑一次（StrictMode 下 effect 会双跑，不能连发两次确认请求）
  const autoConfirmedRef = useRef(false);

  useEffect(() => {
    if (!open) return;
    setStep('credentials');
    setAuth(null);
    setUsername('');
    setError('');
    setPending(false);
    setSubmitting(false);
    setConfirming(false);
    sessionRef.current = '';
    autoConfirmedRef.current = false;
  }, [open]);

  const handleStart = async () => {
    let formValues: JlrAuthFormValues;
    try {
      formValues = await form.validateFields();
    } catch {
      return; // 表单校验失败，Form 已就地提示
    }
    setSubmitting(true);
    setError('');
    setPending(false);
    try {
      const result = await startJlrAuth(releaseId, {
        username: formValues.username.trim(),
        password: formValues.password,
      });
      sessionRef.current = result.session_id;
      setAuth(result);
      setUsername(formValues.username);
      setStep('approve');
    } catch (e) {
      setError(errorBody(e).message || t('启动认证失败，请检查账号密码后重试'));
    } finally {
      setSubmitting(false);
    }
  };

  /**
   * 确认登录。成功即关闭本弹窗进入第二步；尚未批准（409 auth_pending）时
   * 留在弹窗里，用户可以再点一次。
   */
  const handleConfirm = async () => {
    const sessionId = sessionRef.current;
    if (!sessionId) return;
    setConfirming(true);
    setError('');
    setPending(false);
    try {
      await confirmJlrAuth(releaseId, sessionId);
      onAuthenticated(sessionId);
    } catch (e) {
      const body = errorBody(e);
      if (body.error === 'auth_pending') {
        // 手机还没批准：这不是错误，等用户操作完再点一次
        setPending(true);
      } else {
        // 会话失效或已结束：退回账号密码步骤重新来一遍
        setError(body.message || t('认证已失效，请重新输入账号密码'));
        setStep('credentials');
      }
    } finally {
      setConfirming(false);
    }
  };

  // 拿到号码（或后端说明已直接登录）后立即确认一次：用户如果批准得快，
  // 弹窗会自动关闭，不必再点一次按钮。
  useEffect(() => {
    if (!open || step !== 'approve' || !sessionRef.current) return;
    if (autoConfirmedRef.current) return;
    autoConfirmedRef.current = true;
    void handleConfirm();
    // handleConfirm 只读 ref 与 setState，故意只跟 step/open 绑定
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, step]);

  return (
    <Modal
      title={t('上传到 JLR SharePoint')}
      open={open}
      onCancel={onCancel}
      footer={null}
      destroyOnHidden
      maskClosable={false}
      width={620}
    >
      {step === 'credentials' ? (
        <Form
          form={form}
          layout="vertical"
          autoComplete="off"
          preserve={false}
          initialValues={{ username, password: '' }}
          onFinish={handleStart}
        >
          <Alert
            type="info"
            showIcon
            style={{ marginBottom: 16 }}
            message={t('该平台需要 Microsoft 账号认证')}
            description={t('提交账号密码后，系统会生成一个校验数字；请在手机的 Microsoft Authenticator 里输入该数字并批准，登录成功后即可选择上传路径。密码仅在本次请求中转发，不会保存在 BSP。')}
          />
          <Form.Item
            label={t('用户名')}
            name="username"
            rules={[{ required: true, whitespace: true, message: t('请填写账号') }]}
          >
            <Input placeholder={t('例如 wenqiang.zhao.momenta@jlrglobal.onmicrosoft.com')} autoComplete="off" />
          </Form.Item>
          <Form.Item
            label={t('密码')}
            name="password"
            rules={[{ required: true, message: t('请填写密码') }]}
          >
            <Input.Password placeholder={t('Microsoft 账号密码')} autoComplete="new-password" />
          </Form.Item>

          {error && <Alert type="error" showIcon message={error} style={{ marginBottom: 12 }} />}

          <Space className="flex justify-end w-full">
            <Button onClick={onCancel}>{t('取消')}</Button>
            <Button type="primary" icon={<KeyOutlined />} loading={submitting} onClick={handleStart}>
              {t('提交并获取校验数字')}
            </Button>
          </Space>
        </Form>
      ) : (
        <div>
          <Alert
            type="warning"
            showIcon
            icon={<SafetyCertificateOutlined />}
            message={t('请在 Microsoft Authenticator 中批准登录')}
            description={
              auth?.sign_number
                ? t('打开手机上的 Microsoft Authenticator，在弹窗里输入下面这个数字，然后点「批准」。批准后这里会自动进入下一步。')
                : t('认证已直接完成，正在确认登录状态…')
            }
            style={{ marginBottom: 16 }}
          />

          {auth?.sign_number && (
            <div className="text-center my-6">
              <Text type="secondary">{t('校验数字')}</Text>
              <div
                className="font-bold tracking-widest"
                style={{ fontSize: 48, lineHeight: 1.1, letterSpacing: '0.25em' }}
              >
                {auth.sign_number}
              </div>
            </div>
          )}

          {pending && (
            <Alert
              type="info"
              showIcon
              style={{ marginBottom: 12 }}
              message={t('尚未收到批准')}
              description={t('请在手机上完成批准后，点击下方「我已批准，继续」。')}
            />
          )}
          {error && <Alert type="error" showIcon message={error} style={{ marginBottom: 12 }} />}

          <Space className="flex justify-end w-full">
            <Button onClick={() => setStep('credentials')} disabled={confirming}>
              {t('返回修改')}
            </Button>
            <Button type="primary" loading={confirming} onClick={() => void handleConfirm()}>
              {t('我已批准，继续')}
            </Button>
          </Space>
        </div>
      )}
    </Modal>
  );
};

export default JlrAuthModal;
